import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { User } from '../types';
import { auth, db } from '../services/firebase';
import { chatService } from '../services/chatService';
import { GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

interface AuthContextType {
  user: User | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  isLoggingOut: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }

      if (firebaseUser) {
        setLoading(true);
        setError(null);
        try {
          // Whitelist check
          const whitelistRef = doc(db, 'whitelist', firebaseUser.email!);
          const whitelistSnap = await getDoc(whitelistRef);

          if (!whitelistSnap.exists()) {
            throw new Error("auth/unauthorized-access");
          }

          // Update last sign-in time in Firestore
          if (firebaseUser.metadata.lastSignInTime) {
            const userRef = doc(db, 'users', firebaseUser.uid);
            await setDoc(userRef, { 
              lastSignInTime: new Date(firebaseUser.metadata.lastSignInTime).getTime() 
            }, { merge: true });
          }

          await chatService.updateUserStatus(firebaseUser.uid, 'online');
          
          const unsub = chatService.subscribeToUser(firebaseUser.uid, (updatedUser) => {
            setUser(updatedUser);
          });

          unsubRef.current = unsub;

        } catch (err: any) {
          console.error('Auth State Change Error', err);
          setError(err.message || 'Failed to sync user data');
          // If Firestore fails, we still have the basic user info
          setUser(null); // Ensure user is null on error
          await signOut(auth); // Sign out on error
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
        setLoading(false);
      }
    });
    return () => {
      if (unsubRef.current) {
        unsubRef.current();
      }
      unsubscribe();
    };
  }, []);

  const login = async () => {
    if (!auth) return;
    setLoading(true);
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // onAuthStateChanged will handle the rest
    } catch (err: any) {
      console.error("Login Error", err);
      setError(err.message || 'Login failed');
      setUser(null);
      setLoading(false);
    }
  };

  const logout = async () => {
    if (!auth) return;
    setIsLoggingOut(true);
    try {
      if (user) {
        await chatService.updateUserStatus(user.id, 'offline');
      }
    } catch (err: any) {
      console.error("Logout Error: Failed to update status", err);
      // Don't block logout if status update fails
    } finally {
      await signOut(auth);
      setUser(null);
      setIsLoggingOut(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isLoggingOut, error }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
